import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { getIn, useFormikContext } from "formik";
import React, { useEffect } from "react";
import { Card } from "react-bootstrap";

import { IGivenOnRentProperties } from "../../../../config/interfaces";
import { useAppDispatch } from "../../../../redux/Store";
import { showHintModal } from "../../../../redux/slice/HintModalSlice";
import { UtilService } from "../../../../services";
import FASvgIcon from "../../../FASvgIcon";
import PriceDisplay from "../../../PriceDisplay";
import { FormField, NumericInputField, SelectInputField } from "../../../fields";

type Props = {};

const RITaxSavingDeductionForm: React.FC<Props> = (props: Props) => {
  const { values, errors, touched, setFieldValue, handleBlur } =
    useFormikContext<IGivenOnRentProperties>();

  const dispatch = useAppDispatch();
  const ownerShare: number = Number(values.OwnerDetails[0].Percentage) / 100;

  useEffect(() => {
    getTotalDeduction(
      values.PropertyType,
      values.StandardDeductionUs24A,
      values.InterestPreConstruction,
      values.InterestPostConstruction,
      200000
    );
  }, [values.PropertyType, values.OwnerDetails]);

  const getTotalDeduction = (
    propertyType: string,
    value1: string,
    value2: string,
    value3: string,
    maximumLimit: number
  ) => {
    let totalDeductions = 0;

    totalDeductions += Number(value1 || 0);
    totalDeductions += Number(value2 || 0) * 0.2 * ownerShare;

    let interestOnLoan = 0;
    if (propertyType === "Let Out Property" && Number(value3) >= maximumLimit) {
      interestOnLoan = maximumLimit;
    } else {
      interestOnLoan = Number(value3 || 0);
    }

    totalDeductions += interestOnLoan * ownerShare;

    /* if (totalDeductions >= maximumLimit) {
      totalDeductions = maximumLimit;
    } */

    setFieldValue("TotalDeduction", totalDeductions);

    // Net Taxable Income
    setFieldValue("NetTaxableIncome", Number(values?.NetIncome || 0) - totalDeductions);
  };
  return (
    <>
      <section className="mb-5">
        <Card className="more-info-card shadow-none">
          <Card.Header>
            <Card.Title>Tax Saving Deductions</Card.Title>
          </Card.Header>
          <Card.Body>
            <section>
              <FormField label="A. Standard Deduction u/s 24(a) (30% of Net Income)" sm={6}>
                <NumericInputField
                  showLeftIcon={true}
                  name="StandardDeductionUs24A"
                  value={values.StandardDeductionUs24A}
                  onBlur={handleBlur("StandardDeductionUs24A")}
                  onChange={(value) => setFieldValue("StandardDeductionUs24A", value)}
                  isInvalid={
                    Boolean(errors.StandardDeductionUs24A) && touched.StandardDeductionUs24A
                  }
                  errorMsg={errors.StandardDeductionUs24A}
                  isDisabled={true}
                />
              </FormField>

              <FormField
                label={
                  <>
                    Financial Year prior to completion of construction
                    <span
                      className="info-wrap"
                      role="button"
                      onClick={() => {
                        dispatch(
                          showHintModal({
                            title: "FY previous to completion of construction",
                            componentName: "FYPriorConstructionHint"
                          })
                        );
                      }}
                    >
                      <FASvgIcon icon={faInfoCircle} />
                    </span>
                  </>
                }
                sm={6}
              >
                <SelectInputField
                  placeholder="Select"
                  name={`FinancialYear`}
                  isOptionArrayOfObject={false}
                  options={UtilService.generateYearRanges({
                    start: 2018,
                    end: new Date().getFullYear() - 1
                  })}
                  value={[values.FinancialYear]}
                  onChange={(selectedOption) => {
                    if (selectedOption) {
                      setFieldValue(`FinancialYear`, selectedOption.value);
                    } else {
                      setFieldValue(`FinancialYear`, undefined);
                    }
                  }}
                  onBlur={handleBlur(`FinancialYear`)}
                  isInvalid={
                    Boolean(getIn(errors, `FinancialYear`)) && getIn(touched, `FinancialYear`)
                  }
                  errorMsg={getIn(errors, `FinancialYear`)}
                  note="Ex: If construction completed on 17th July, 2010, you need to specify 2009-10"
                />
              </FormField>

              <FormField
                isRequired={true}
                label={
                  <>
                    B. Interest Paid during pre-construction period
                    <span
                      className="info-wrap"
                      role="button"
                      onClick={() => {
                        dispatch(
                          showHintModal({
                            title: "Interest paid during pre-construction period",
                            componentName: "InterestPaidPreConstructionHint"
                          })
                        );
                      }}
                    >
                      <FASvgIcon icon={faInfoCircle} />
                    </span>
                  </>
                }
                sm={6}
              >
                <NumericInputField
                  showLeftIcon={true}
                  name="InterestPreConstruction"
                  value={values.InterestPreConstruction}
                  onBlur={handleBlur("InterestPreConstruction")}
                  onChange={(value) => {
                    setFieldValue("InterestPreConstruction", value);
                    getTotalDeduction(
                      values.PropertyType,
                      values.StandardDeductionUs24A,
                      value,
                      values.InterestPostConstruction,
                      200000
                    );
                  }}
                  isInvalid={
                    Boolean(errors.InterestPreConstruction) && touched.InterestPreConstruction
                  }
                  errorMsg={errors.InterestPreConstruction}
                  note="20% of the interest paid can be deducted as per taxation rules."
                />
              </FormField>

              <FormField
                isRequired={true}
                label={
                  <>
                    C. Interest Payable on housing loan
                    <span
                      className="info-wrap"
                      role="button"
                      onClick={() => {
                        dispatch(
                          showHintModal({
                            title: "Interest paid post construction(section 24(b) deduction)",
                            componentName: "InterestPaidPostConstructionHint"
                          })
                        );
                      }}
                    >
                      <FASvgIcon icon={faInfoCircle} />
                    </span>
                  </>
                }
                sm={6}
              >
                <NumericInputField
                  showLeftIcon={true}
                  name="InterestPostConstruction"
                  value={values.InterestPostConstruction}
                  onBlur={handleBlur("InterestPostConstruction")}
                  onChange={(value) => {
                    setFieldValue("InterestPostConstruction", value);
                    getTotalDeduction(
                      values.PropertyType,
                      values.StandardDeductionUs24A,
                      values.InterestPreConstruction,
                      value,
                      200000
                    );
                  }}
                  isInvalid={
                    Boolean(errors.InterestPostConstruction) && touched.InterestPostConstruction
                  }
                  errorMsg={errors.InterestPostConstruction}
                />
              </FormField>

              <FormField label="D. Total Deduction(A + 20% of B + C)" sm={6}>
                <NumericInputField
                  showLeftIcon={true}
                  name="TotalDeduction"
                  value={values.TotalDeduction}
                  onBlur={handleBlur("TotalDeduction")}
                  onChange={(value) => setFieldValue("TotalDeduction", value)}
                  isInvalid={Boolean(errors.TotalDeduction) && touched.TotalDeduction}
                  errorMsg={errors.TotalDeduction}
                  note={
                    <>
                      Maximum deduction that can be claimed in a FY is{" "}
                      <PriceDisplay price={200000} decimalUpto={0} />
                    </>
                  }
                  isDisabled={true}
                />
              </FormField>
            </section>
          </Card.Body>
        </Card>
      </section>
    </>
  );
};

export default RITaxSavingDeductionForm;
